'use client';

import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useRef } from 'react';

import { GTMPromotion, sendViewPromotion } from '../utils/bannerPromotion';

export const useGTMViewPromotionCarousel = (
  getPromotion: (index: number) => GTMPromotion | undefined
) => {
  const sentIndices = useRef(new Set<number>());
  const cleanupRef = useRef<(() => void) | null>(null);
  const isCarouselVisible = useRef(false);

  const trackSlidesInView = useCallback(
    (api: EmblaCarouselType) => {
      if (!isCarouselVisible.current) return;

      api.slidesInView().forEach((index) => {
        if (sentIndices.current.has(index)) return;

        const promotion = getPromotion(index);
        if (!promotion) return;

        sentIndices.current.add(index);

        sendViewPromotion(promotion);
      });
    },
    [getPromotion]
  );

  const onCarouselInit = useCallback(
    (api: EmblaCarouselType) => {
      cleanupRef.current?.();

      const handleSlidesInView = () => trackSlidesInView(api);

      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          isCarouselVisible.current = entry.isIntersecting;

          if (entry.isIntersecting) {
            handleSlidesInView();
          }
        },
        { threshold: 0.5 }
      );

      visibilityObserver.observe(api.rootNode());

      api.on('slidesInView', handleSlidesInView).on('reInit', handleSlidesInView);

      cleanupRef.current = () => {
        api.off('slidesInView', handleSlidesInView).off('reInit', handleSlidesInView);
        visibilityObserver.disconnect();
        isCarouselVisible.current = false;
      };
    },
    [trackSlidesInView]
  );

  useEffect(() => () => cleanupRef.current?.(), []);

  return onCarouselInit;
};
